import { useEffect, useMemo, useState } from 'react'

import { apiGetSiteWorksPartners } from '@/features/jobs/api/api-get-site-works-partners'
import { apiGetUsers } from '@/features/jobs/api/api-get-users'
import { formatJobSeverity } from '@/features/jobs/lib/format-job-severity'
import { formatJobStatus } from '@/features/jobs/lib/format-job-status'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import { JOB_OUTCOMES } from '@/features/jobs/lib/job-outcome-options'
import {
  emptyJobFilters,
  type JobFilters,
  type JobOutcomeFilter,
  type JobStatusFilter,
  type JobTypeFilter,
} from '@/features/jobs/types/job-filters'
import type { JobSeverity, JobType } from '@/features/jobs/types/job'
import type { FilterOption } from '@/lib/api/types'
import {
  Button,
  InputField,
  Offcanvas,
  SelectField,
  type SelectOption,
} from '@/shared/components'

type JobFiltersPanelProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: JobFilters
  onApply: (filters: JobFilters) => void
}

const jobTypeOptions: SelectOption[] = (
  ['new_installation', 'remediation'] as JobType[]
).map((jobType) => ({
  value: jobType,
  label: formatJobType(jobType),
}))

const severityOptions: SelectOption[] = (
  ['expedited', 'urgent', 'bau'] as JobSeverity[]
).map((severity) => ({
  value: severity,
  label: formatJobSeverity(severity),
}))

const statusOptions: SelectOption[] = (
  [
    'open',
    'in_progress',
    'completed',
    'aborted',
    'cancelled',
  ] as JobStatusFilter[]
).map((status) => ({
  value: status,
  label: formatJobStatus(status),
}))

const outcomeOptions: SelectOption[] = JOB_OUTCOMES.map((outcome) => ({
  value: outcome.value,
  label: outcome.label,
}))

const toSelectOptions = (options: FilterOption[]): SelectOption[] =>
  options.map((option) => ({
    value: String(option.id),
    label: option.label,
  }))

export const JobFiltersPanel = ({
  open,
  onOpenChange,
  filters,
  onApply,
}: JobFiltersPanelProps) => {
  const [draft, setDraft] = useState<JobFilters>(filters)
  const [raisedByUserOptions, setRaisedByUserOptions] = useState<FilterOption[]>(
    []
  )
  const [completedByUserOptions, setCompletedByUserOptions] = useState<
    FilterOption[]
  >([])
  const [partnerOptions, setPartnerOptions] = useState<FilterOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [optionsError, setOptionsError] = useState<string | null>(null)

  const [wasOpen, setWasOpen] = useState(open)

  if (open !== wasOpen) {
    setWasOpen(open)

    if (open) {
      setDraft(filters)
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }

    const loadFilterOptions = async () => {
      setIsLoadingOptions(true)
      setOptionsError(null)

      try {
        const [raisedByUsers, completedByUsers, partners] = await Promise.all([
          apiGetUsers({ exclude_role: 'site_worker' }),
          apiGetUsers({ role: 'site_worker' }),
          apiGetSiteWorksPartners(),
        ])

        setRaisedByUserOptions(raisedByUsers)
        setCompletedByUserOptions(completedByUsers)
        setPartnerOptions(partners)
      } catch (loadError) {
        setOptionsError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load filter options'
        )
      } finally {
        setIsLoadingOptions(false)
      }
    }

    void loadFilterOptions()
  }, [open])

  const raisedByUserSelectOptions = useMemo(
    () => toSelectOptions(raisedByUserOptions),
    [raisedByUserOptions]
  )

  const completedByUserSelectOptions = useMemo(
    () => toSelectOptions(completedByUserOptions),
    [completedByUserOptions]
  )

  const partnerSelectOptions = useMemo(
    () => toSelectOptions(partnerOptions),
    [partnerOptions]
  )

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    const cleared = emptyJobFilters()
    setDraft(cleared)
    onApply(cleared)
    onOpenChange(false)
  }

  return (
    <Offcanvas
      open={open}
      onOpenChange={onOpenChange}
      title="Filter jobs"
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={handleClear}
          >
            Clear
          </Button>

          <Button
            type="button"
            variant="action"
            className="flex-1"
            onClick={handleApply}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <InputField
          id="job-filter-date-raised"
          label="Date raised"
          type="date"
          value={draft.date_raised}
          onChange={(date_raised) =>
            setDraft((current) => ({ ...current, date_raised }))
          }
        />

        <SelectField
          id="job-filter-raised-by"
          label="Raised by"
          value={
            draft.raised_by_user_id
              ? String(draft.raised_by_user_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              raised_by_user_id: value ? Number(value) : undefined,
            }))
          }
          options={raisedByUserSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        <SelectField
          id="job-filter-job-type"
          label="Job type"
          value={draft.job_type}
          onValueChange={(job_type) =>
            setDraft((current) => ({
              ...current,
              job_type: job_type as JobTypeFilter | undefined,
            }))
          }
          options={jobTypeOptions}
          placeholder="All"
          allowEmpty
        />

        <InputField
          id="job-filter-location"
          label="Location"
          placeholder="Search by location"
          value={draft.location}
          onChange={(location) =>
            setDraft((current) => ({ ...current, location }))
          }
        />

        <SelectField
          id="job-filter-site-works-partner"
          label="SiteWorks partner"
          value={
            draft.assigned_partner_id
              ? String(draft.assigned_partner_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              assigned_partner_id: value ? Number(value) : undefined,
            }))
          }
          options={partnerSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        <SelectField
          id="job-filter-severity"
          label="Severity"
          value={draft.severity}
          onValueChange={(severity) =>
            setDraft((current) => ({
              ...current,
              severity: severity as JobSeverity | undefined,
            }))
          }
          options={severityOptions}
          placeholder="All"
          allowEmpty
        />

        <SelectField
          id="job-filter-status"
          label="Status"
          value={draft.status}
          onValueChange={(status) =>
            setDraft((current) => ({
              ...current,
              status: status as JobStatusFilter | undefined,
            }))
          }
          options={statusOptions}
          placeholder="All"
          allowEmpty
        />

        <SelectField
          id="job-filter-outcome"
          label="Outcome"
          value={draft.outcome}
          onValueChange={(outcome) =>
            setDraft((current) => ({
              ...current,
              outcome: outcome as JobOutcomeFilter | undefined,
            }))
          }
          options={outcomeOptions}
          placeholder="All"
          allowEmpty
        />

        <InputField
          id="job-filter-completed-at"
          label="Outcome completed date"
          type="date"
          value={draft.completed_at}
          onChange={(completed_at) =>
            setDraft((current) => ({ ...current, completed_at }))
          }
        />

        <SelectField
          id="job-filter-completed-by"
          label="Outcome completed by"
          value={
            draft.completed_by_user_id
              ? String(draft.completed_by_user_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              completed_by_user_id: value ? Number(value) : undefined,
            }))
          }
          options={completedByUserSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        {optionsError ? (
          <p className="text-sm text-destructive" role="alert">
            {optionsError}
          </p>
        ) : null}
      </div>
    </Offcanvas>
  )
}
